import React, {useEffect, useState, useRef} from "react";
import GenericLayout from "layouts/generic";
import css from "./index.module.scss";
import ContactSection from "sections/contact";
import { useInView } from "react-intersection-observer";
import { gsap } from "scripts/gsap";
import SplitText from "@/components/splitText";
import slideFadeIn from "scripts/animations/slideFadeIn";
import inViewSettings from "scripts/animations/inViewSettings";
import TacticsSmall, {TACTICS} from "@/sections/tacticsSmall";
import TestimonialsSpotlight, {TESTIMONIALS} from "@/sections/testimonialsSpotlight";
import Fold1Section from "@/sections/fold1";
import BasicCardSection from "@/sections/basicCards";
import ChartSvg from "./chart.svg";
import FlowChartSection from "@/sections/flowChart";
import PenNibSvg from "/public/images/icons/penNib.svg";
import MagnifyingGlassChartSvg from "/public/images/icons/magnifyingGlassChart.svg";
import SignsPostSvg from "/public/images/icons/signsPost.svg";
import MapLocationDotSvg from "/public/images/icons/mapLocationDot.svg";
import ArrowsSpinSvg from "/public/images/icons/arrowsSpin.svg";
import ImageCard from "@/components/imageCard";
import popFadeIn from "scripts/animations/popFadeIn";
import Picture from "@/components/picture";

const ads = [
  {
    name: "UpPaint",
    body: <>UpPaint is a brand of recycled paint. We designed a series of ads for their Facebook campaign, which resulted in a <b>$0.35</b> cost-per-click in the USA.</>,
    image: "/images/misc/villa/villa",
  },
  {
    name: "Native",
    body: <>We tested a lot of micro-changes for ads in 12 different languages around the world to drive signups for data collectors. The final ad had a <b>$0.04 CPC</b> and <b>3.21% CTR</b>.</>,
    image: "/images/misc/native/native",
  },
  {
    name: "Caden",
    body: <>This &quot;Zuck Dunk&quot; ad went viral with app waitlist signups converting for under $1. The ad collected over 1.4k comments and was shared over 1.2k times before Meta shut it down for violating their &quot;Dating&quot; policy — though we suspect it was shut down for other reasons.</>,
    image: "/images/misc/unzuck/unzuck",
  },
  {
    name: "Radix",
    body: <>Radix is a domain registrar. Over a 3-week period, our ad campaign led to <b>110,427</b> new email signups.</>,
    image: "/images/misc/followTheWhiteRabbit/followTheWhiteRabbit",
  },
];

export default function HighPerformanceAdCreatives({}) {
  const [elements, setElements] = useState({
    fold: {
      infographic: useInView(inViewSettings((targets) => {
        let chart = targets[0];
        let tl = gsap.timeline({});

        let dial1 = chart.querySelectorAll("#dial1 path");
        let dial2 = chart.querySelectorAll("#dial2 path");
        let dial1Text = chart.querySelectorAll(".dial1Text");
        let dial2Text = chart.querySelectorAll(".dial2Text");
        let explanation = chart.querySelectorAll(`.${css.explanation}`);
        let attribution = chart.querySelectorAll(`.${css.attribution}`);

        tl.fromTo(chart, {
          visibility: "visible",
          opacity: 0,
        }, {
          opacity: 1,
          duration: 0.25,
          ease: "sine.inOut",
        }, 0);



        tl.fromTo(dial1Text, {
          opacity: 0,
        }, {
          opacity: 1,
          duration: 0.25,
          ease: "sine.inOut",
          stagger: {
            each: 0.1,
          },
        }, "<25%");

        tl.fromTo(dial1, {
          rotate: 0,
          transformOrigin: "50% 0%",
        }, {
          rotate: 180,
          duration: 1,
          ease: "sine.inOut",
        }, "<25%");



        tl.fromTo(dial2Text, {
          opacity: 0,
        }, {
          opacity: 1,
          duration: 0.25,
          ease: "sine.inOut",
          stagger: {
            each: 0.1,
          },
        }, "<15%");

        tl.fromTo(dial2, {
          rotate: 0,
          transformOrigin: "50% 0%",
        }, {
          rotate: 180,
          duration: 1,
          ease: "sine.inOut",
        }, "<25%");


        tl.fromTo(explanation, {
          opacity: 0,
        }, {
          opacity: 1,
          duration: 0.2,
          ease: "sine.inOut",
        }, "<25%");

        tl.fromTo(attribution, {
          opacity: 0,
        }, {
          opacity: 1,
          duration: 0.2,
          ease: "sine.inOut",
        }, "<25%");
      })),
    },
    roi: {
      text: useInView(inViewSettings(slideFadeIn)),
      headline: useInView(inViewSettings(slideFadeIn)),
    },
    platform: {
      text: useInView(inViewSettings(slideFadeIn)),
      headline: useInView(inViewSettings(slideFadeIn)),
    },
    ads: {
      headline: useInView(inViewSettings(slideFadeIn)),
      cards: useInView(inViewSettings(popFadeIn)),
    },
  });

  return <GenericLayout
    title="High Performance Ad Creatives | Deviate Labs"
    description="Our expert ad team develops breakthrough creatives tailored to your brand's unique identity and goals."
    color="purple"
  >
    <Fold1Section
      headline={<>
        <div>High Performance</div>
        <div>Ad Creatives</div>
      </>}
      className={css.fold}
      subheadline="Our expert ad team develops breakthrough creatives tailored to your brand's unique identity and goals."
      image={
        <div className={`target ${css.infographic}`} ref={elements.fold.infographic.ref}>
          <div className={css.chart}><ChartSvg/></div>

          <div className={css.explanation}>
            <div>If companies committed the ideal amount of ad resources, their ROI could jump <span>50%</span></div>
          </div>

          <div className={css.attribution}>From <a target="_blank" href="https://global.nielsen.com/wp-content/uploads/sites/2/2022/07/ROI-Report-2022-MediaSellers-English.pdf">Nielsen</a>.</div>
        </div>
      }
    />


    <BasicCardSection
      headline="The Deviate Labs Difference"
      subheadline="We craft ad creatives that captivate, convert, and create lasting impressions."
      width="2"
      color="cyan"
      cards={[
        {
          name: "Data-Driven Insights",
          body: <>We dissect market trends and user behavior, ensuring your creatives are not just visually stunning, but strategically sound.</>,
        },
        {
          name: "Flawless Design",
          body: <>No cookie-cutter templates. Your brand deserves unique representation, and we design with your brand&apos;s essence at the forefront.</>,
        },
        {
          name: "Proven Performance",
          body: <>We don&apos;t just design, we test. Our creatives undergo rigorous evaluations to ensure they&apos;re optimized for your audience.</>,
        },
        {
          name: "End-To-End Collaboration",
          body: <>Your company, our expertise. Together, we&apos;ll co-create ad content that truly represents your brand and resonates with your audience..</>,
        },
      ]}
    />

    <section className={`section ${css.solution}`}>
      <div className="sectionInner">
        <h2 ref={elements.roi.headline.ref}>
          <SplitText className="target" text="ROI Is Our North Star"/>
        </h2>

        <div className={css.text} ref={elements.roi.text.ref}>
          <p>
            <SplitText className="target" text="Beautiful design means nothing if it doesn't deliver real business results. That's why Deviate Labs focuses on strategy-led creativity that boosts key metrics for clients across industries. From 10x growth in conversions to lifting brand favorability by 20+ points, our custom ad creatives get results."/>
          </p>
          <p>
            <SplitText className="target" text="We closely track performance and continuously optimize based on in-depth analytics. This enables us to push engagement and conversions even higher over the course of a campaign. ROI isn't an afterthought - it's built into our process from start to finish."/>
          </p>
        </div>
      </div>
    </section>


    <section className={`section ${css.platform}`}>
      <div className="sectionInner">
        <h2 ref={elements.platform.headline.ref}>
          <SplitText className="target" text="Mighty at Any Size for Any Platform"/>
        </h2>

        <div className={css.text} ref={elements.platform.text.ref}>
          <p>
            <SplitText className="target" text="Discover unparalleled adaptability with our ad creatives that shine across every medium — be it the immersive storytelling on Instagram stories, influential visuals on Facebook feeds, or persuasive narratives on website homepages. Our expert team plans striking content, perfectly optimized for different use-cases across the digital spectrum. With detailed, nuanced knowledge of what works for various platforms and devices, we translate your message into a cross-platform success story that engages your audience, wherever they are."/>
          </p>
        </div>
      </div>
    </section>


    <FlowChartSection
      headline="From Insights to Eye-Catching Ads"
      subheadline="Our strategic, data-driven process enables us to consistently create high-performing ad creatives that captivate audiences and deliver results."
      cards={[
        {
          headline: "Step 1: Discovery",
          body: "First, we dive deep into understanding your business, target audiences, competitors, and objectives. Our research informs every next step.",
          icon: <MapLocationDotSvg/>,
        },
        {
          headline: "Step 2: Strategization",
          body: "Leveraging insights from Discovery, we develop strategies and concepts for ad campaigns optimized for your goals.",
          icon: <SignsPostSvg/>,
        },
        {
          headline: "Step 3: Creation",
          body: "With strategies set, our talented design team brings campaigns to life through iteration and testing. We craft aesthetically stunning ads fine-tuned for your brand.",
          icon: <PenNibSvg/>,
        },
        {
          headline: "Step 4: Analysis",
          body: "We closely track campaign performance using key metrics and analytics. This allows us to identify opportunities and make optimizations.",
          icon: <MagnifyingGlassChartSvg/>,
        },
        {
          headline: "Step 5: Optimization",
          body: "Our work doesn't end at launch. We continuously refine campaigns based on data to boost KPIs throughout the ad lifecycle.",
          icon: <ArrowsSpinSvg/>,
        },
      ]}
    />



    <section className={`section ${css.ads}`}>
      <div className="sectionInner">
        <h2 ref={elements.ads.headline.ref}>
          <SplitText className="target" text="Sample Ad Creatives"/>
        </h2>
        <div className={css.cards} ref={elements.ads.cards.ref}>
          {ads.map((ad) => <div className={`target ${css.wrapper}`} key={ad.name}>

            <div className={css.card}>
              <div className={css.image}>
                <Picture
                  base={ad.image}
                  alt=""
                  exts={["webp", "png"]}
                  widths={[100, 200, 300, 400, 500, 600, 700, 800, 900, 1000]}
                  loading="lazy"
                  sizes="(max-width: 1000px) 100vw, 25vw"
                />
              </div>
              <div className={css.body}>
                <div className={css.headline}>{ad.name}</div>
                <p>{ad.body}</p>
              </div>
            </div>

          </div>)}
        </div>
      </div>
    </section>
    <TestimonialsSpotlight testimonial={TESTIMONIALS.UPPAINT}/>

    <TacticsSmall
      enabled={[
        TACTICS.HIGH_PERFORMANCE_LANDING_PAGES,
        TACTICS.CONTENT_MARKETING,
        TACTICS.INFLUENCER_MARKETING,
        TACTICS.SONIC_BRANDING,
        TACTICS.B2B_SAAS_GROWTH_MARKETING,
      ]}
    />

    <ContactSection/>


  </GenericLayout>;
}
